import * as React from 'react';

import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';
import { isDarkSurface } from '@/lib/customer-skin';
import { cn } from '@/lib/utils';

export type CustomerLogoVariant = 'brand' | 'inline' | 'compact';

const variantClasses: Record<CustomerLogoVariant, string> = {
  brand: 'max-h-8 max-w-28',
  inline: 'max-h-10 max-w-20 sm:max-h-11 sm:max-w-28 lg:max-w-36',
  compact: 'max-h-full max-w-full'
};

/**
 * Renders the uploaded customer logo with the configured presentation.
 *
 * The brand mark enforces the configured minimum displayed width and clear
 * space; compact contexts keep their container sizing so a large minimum can
 * never break a small chip. Every variant honours the dark-surface rule by
 * placing the artwork on a light plate when configuration asks for one. The
 * artwork itself is never filtered, recoloured or stretched.
 */
export function CustomerLogoMark({
  variant = 'brand',
  alt,
  className
}: {
  variant?: CustomerLogoVariant;
  alt?: string;
  className?: string;
}) {
  const { logo, skin, header, theme } = useCustomerSkin();
  if (!logo) return null;

  const presentation = skin?.logo ?? {};
  const minWidth = variant === 'brand' ? presentation.minWidth ?? null : null;
  const clearSpace = variant === 'brand' ? presentation.clearSpace ?? null : null;
  const surfaceIsDark = header ? isDarkSurface(header.background) : theme === 'dark';
  const plate = surfaceIsDark && presentation.onDark === 'light-plate';

  const style: React.CSSProperties = {};
  if (minWidth) {
    style.minWidth = `${minWidth}px`;
    // Height follows the artwork's proportions and stays inside the header band.
    style.maxHeight = '3rem';
  }
  if (clearSpace) {
    style.padding = `${clearSpace}px`;
  }

  const image = (
    <img
      src={logo.dataUrl}
      alt={alt ?? logo.altText}
      className={cn('object-contain', minWidth ? undefined : variantClasses[variant], className)}
      style={style}
    />
  );

  return plate ? (
    <span className="inline-flex items-center justify-center rounded-md bg-white">{image}</span>
  ) : (
    image
  );
}
